import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { View } from 'react-big-calendar';

export interface CalendarFilters {
  view?: View;
  date?: string;
  createdById?: number;
  attendeeId?: number;
  clientId?: number;
  matterId?: number;
  target?: string;
}

const DEFAULT_VIEW: View = 'month';

export function useCalendarFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<CalendarFilters>(() => {
    const parsed: CalendarFilters = {};

    const view = searchParams.get('view');
    if (view) parsed.view = view as View;

    const date = searchParams.get('date');
    if (date) parsed.date = date;

    const createdById = searchParams.get('createdById');
    if (createdById) parsed.createdById = Number(createdById);

    const attendeeId = searchParams.get('attendeeId');
    if (attendeeId) parsed.attendeeId = Number(attendeeId);

    const clientId = searchParams.get('clientId');
    if (clientId) parsed.clientId = Number(clientId);

    const matterId = searchParams.get('matterId');
    if (matterId) parsed.matterId = Number(matterId);

    const target = searchParams.get('target');
    if (target) parsed.target = target;

    return parsed;
  }, [searchParams]);

  const updateFilter = useCallback(
    <K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (value === undefined || value === null || value === '') {
          newParams.delete(String(key));
        } else {
          newParams.set(String(key), String(value));
        }

        // Reset matterId when clientId changes
        if (key === 'clientId') {
          newParams.delete('matterId');
        }

        return newParams;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const updateFilters = useCallback(
    (updates: Partial<CalendarFilters>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        });

        return newParams;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const setView = useCallback((view: View) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (view === DEFAULT_VIEW) {
        newParams.delete('view');
      } else {
        newParams.set('view', view);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setDate = useCallback((date: Date) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('date', date.toISOString().split('T')[0]);
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Computed values
  const currentView = filters.view || DEFAULT_VIEW;
  const currentDate = filters.date ? new Date(filters.date) : new Date();

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    setView,
    setDate,
    currentView,
    currentDate,
  };
}
